import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { css } from 'twin.macro';
import { connect } from 'react-redux';
import { FaRobot } from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap'; // Import Bootstrap modal components
import { WebSocketContext } from '../../../_redux/actions/ActionSocket';
import { fetchWrapper, SanitizeHTML, toastWrapper } from '../../../_helpers';
import { BiMinus } from 'react-icons/bi';
import Avatar from '@/components/Admin/reusableComponents/Avatar';
import { GoToChannel } from '@/_elements/Links';
import DynamicChannelSelectFilter from '@/components/Admin/DynamicChannelSelectFilter';
import { HiOutlineSwitchHorizontal } from 'react-icons/hi';
import styled from '@emotion/styled';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { StyledCreateChannelToggle } from '@/components/Home/components/CreateChannelToggle';

const FabMenuContainer = styled.div`
    position: absolute;
    bottom: 6%;
    right: 150px;
    z-index: 999;
`;

const widgetContainer = css`
    position: absolute;
    bottom: 124px;
    right: 24px;
    z-index: 50;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    pointer-events: none;
`;

const widgetPanel = css`
    width: 500px;
    max-width: 90vw;
    height: 420px;
    max-height: 70vh;
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
    overflow: hidden;
    margin-bottom: 8px;
    pointer-events: auto;

    @media (max-width: 768px) {
        width: 100vw;
        max-width: 100vw;
        right: 0;
        left: 0;
        border-radius: 16px 16px 0 0;
    }
`;

const switchChannelButton = css`
    pointer-events: auto;
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    font-size: 1.2rem;
    margin-left: 8px;
    padding: 4px;
    border-radius: 4px;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
    }
`;

const Component = React.memo(({ position, currUser, teams, showBobChat, toggleBobChat }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [hasSentAutoPrompt, setHasSentAutoPrompt] = useState(false);
    const [localMessages, setLocalMessages] = useState([]);
    const [activeChannel, setActiveChannel] = useState({
        title: '',
    });
    const [isBobThinking, setIsBobThinking] = useState(null);
    const [showChannelModal, setShowChannelModal] = useState(false);
    const [selectedChannelOption, setSelectedChannelOption] = useState(null);
    const ws = useContext(WebSocketContext);
    const messagesContainerRef = useRef(null);

    const [channelId, setChannelId] = useState(currUser?.lastSeenChannel || null);

    useEffect(() => {
        if (channelId && activeChannel?.id === channelId) {
            setSelectedChannelOption({
                label: activeChannel.title || `Channel ${channelId}`,
                value: channelId,
            });
        }
    }, [channelId, activeChannel]);

    // Function to parse message data and extract only text
    const parseMessageData = (mData) => {
        let displayMessage = '';
        try {
            const parsed = JSON.parse(mData);
            displayMessage = parsed.text || mData || '';
        } catch (e) {
            displayMessage = mData || '';
        }
        return displayMessage;
    };

    const ensureChannelId = useCallback(async () => {
        if (channelId) return channelId;
        try {
            let selectedChannel = null;

            // First priority: Use currUser.lastSeenChannel if available
            if (currUser?.lastSeenChannel) {
                try {
                    const channelResponse = await fetchWrapper.get(`chatrooms/${currUser.lastSeenChannel}`);
                    if (channelResponse) {
                        selectedChannel = channelResponse;
                    }
                    console.log('channelResponse', channelResponse);
                } catch (e) {
                    console.log('Failed to fetch lastSeenChannel details:', e);
                }
            }

            // Second priority: If no lastSeenChannel or it failed, fetch channels and find payroll channel
            if (!selectedChannel) {
                try {
                    const response = await fetchWrapper.get('chatrooms?sortBy=title:asc&limit=100&page=1');
                    const rooms = response?.results || response?.rooms || [];
                    // Find payroll channel first
                    const payrollChannel = rooms.find((r) => r.title && r.title.toLowerCase().includes('payroll'));

                    if (payrollChannel) {
                        selectedChannel = payrollChannel;
                    } else if (rooms.length > 0) {
                        // If no payroll channel, use the first channel
                        const firstRoom = rooms[0];
                        selectedChannel = firstRoom;
                    }
                } catch (e) {
                    console.log('Failed to fetch channels:', e);
                }
            }

            if (!selectedChannel?.id) {
                toastWrapper.error('Bob Chat channel not found..');
                return null;
            }
            setChannelId(selectedChannel.id);
            setActiveChannel(selectedChannel);

            setSelectedChannelOption({
                label: selectedChannel.title || `Channel ${selectedChannel.id}`,
                value: selectedChannel.id || selectedChannel._id,
            });

            return selectedChannel.id;
        } catch (e) {
            console.error('Error auto-configuring Bob Chat channel', e);
            toastWrapper.error('Unable to configure Bob Chat channel.');
            return null;
        }
    }, [channelId, currUser]);

    const loadChannelDetails = useCallback(async (channelId) => {
        try {
            const response = await fetchWrapper.get(`chatrooms/${channelId}`);
            if (response) {
                setActiveChannel(response);
                setSelectedChannelOption({
                    label: response.title || `Channel ${channelId}`,
                    value: channelId,
                });
            }
        } catch (error) {
            console.error('Failed to load channel details:', error);
            toastWrapper.error(error?.message || 'Unable to load channel details.');
            setActiveChannel({});
        }
    }, []);

    const handleToggleWidget = async () => {
        setIsOpen((prev) => !prev);
        let id = channelId;
        if (!id) {
            id = await ensureChannelId();
        }
        if (!id) return;
        setHasSentAutoPrompt(false);
    };

    useEffect(() => {
        if (showBobChat) {
            handleToggleWidget();
        }
    }, [showBobChat]);

    const handleChannelSelect = (option) => {
        setSelectedChannelOption(option);

        if (option && option.value) {
            const newChannelId = option.value;
            setChannelId(newChannelId);
            setActiveChannel({
                title: option.label,
                id: newChannelId,
            });
            setLocalMessages([]);
            setShowChannelModal(false);
            toastWrapper.success(`Switched to channel: ${option.label}`);
        }
    };

    const loadHistoryForChannel = async (channelIdToLoad) => {
        try {
            const res = await fetchWrapper.get(
                `chatrooms/${channelIdToLoad}/messages?sortBy=createdAt:desc&limit=50&page=1`
            );
            const results = res?.results || [];

            const mapped = results.map((m) => {
                const text = parseMessageData(m.data);
                const isCurrentUser =
                    currUser && ((m.user?.id || m.user?._id) === currUser._id || m.user === currUser._id);

                return {
                    id: m.id,
                    text,
                    isCurrentUser,
                    createdAt: m.createdAt,
                    user: m.user,
                };
            });

            setLocalMessages(mapped);
        } catch (e) {
            console.error('Error loading chat history for channel', e);
        }
    };

    useEffect(() => {
        if (!isOpen || !channelId || !ws || !ws.socket) return;
        ws.joinChatRoom && ws.joinChatRoom(channelId);
        loadChannelDetails(channelId);
    }, [isOpen, channelId, ws]);

    useEffect(() => {
        if (!isOpen || !channelId) return;

        const loadHistory = async () => {
            await loadHistoryForChannel(channelId);
        };

        loadHistory();
    }, [isOpen, channelId, currUser]);

    useEffect(() => {
        if (!isOpen || !channelId || !ws || !ws.socket) return;

        const handler = (message) => {
            try {
                setIsBobThinking(true);
                if (message.isDirectMessage === false && message.room === channelId) {
                    const text = parseMessageData(message.data);

                    // Fixed sender/receiver logic
                    const isCurrentUser =
                        currUser &&
                        ((message.user?.id || message.user?._id) === currUser._id || message.user === currUser._id);

                    const local = {
                        ...message,
                        id: message.id,
                        text,
                        isCurrentUser,
                        createdAt: message.createdAt,
                    };

                    setLocalMessages((prev) => {
                        if (prev.some((m) => m.id === local.id)) return prev;
                        const next = [...prev, local];
                        return next.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    });
                }
                if (message?.user?.username === 'bot_bob') {
                    setIsBobThinking(false);
                }
            } catch (err) {
                console.error('Error handling Bob chat socket message', err);
            } finally {
                setTimeout(() => {
                    setIsBobThinking(false);
                }, 10000);
            }
        };

        ws.socket.on('message', handler);
        return () => {
            if (ws && ws.socket) {
                ws.socket.off('message', handler);
            }
        };
    }, [isOpen, channelId, ws, currUser]);

    useEffect(() => {
        if (!isOpen || !messagesContainerRef.current) return;
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;
    }, [localMessages, isOpen, isBobThinking]);

    const autoPromptText = '';

    const sendToBob = useCallback(
        (rawText) => {
            if (!ws || !ws.sendMessage || !channelId) return;
            const trimmed = (rawText || '').trim();
            if (!trimmed) return;

            const prefix = '/bob';
            const lower = trimmed.toLowerCase();
            const finalText = lower.startsWith(prefix) ? trimmed : `${prefix} ${trimmed}`;
            setIsBobThinking(true);
            ws.sendMessage(finalText, null, position, { chatroomId: channelId });
        },
        [ws, channelId, position]
    );

    useEffect(() => {
        if (!isOpen || hasSentAutoPrompt || !autoPromptText || !channelId) return;
        sendToBob(autoPromptText);
        setHasSentAutoPrompt(true);
    }, [isOpen, hasSentAutoPrompt, autoPromptText, channelId, sendToBob]);

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!inputValue.trim()) return;
        sendToBob(inputValue);
        setInputValue('');
    };

    const ThinkingIndicator = () => (
        <div
            css={css`
                display: flex;
                justify-content: flex-start;
                margin-bottom: 6px;
            `}
        >
            <div
                css={css`
                    max-width: 80%;
                    padding: 8px 12px;
                    border-radius: 12px;
                    font-size: 13px;
                    background-color: #ffffff;
                    color: #222;
                    display: flex;
                    align-items: center;
                    gap: 4px;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                `}
            >
                <span>Bob is thinking</span>
                <span
                    css={css`
                        display: inline-flex;
                        align-items: flex-end;
                        height: 12px;
                    `}
                >
                    <span
                        css={css`
                            display: inline-block;
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background-color: #28637e;
                            margin: 0 1px;
                            animation: bobDot 1.4s infinite ease-in-out both;

                            &:nth-child(1) {
                                animation-delay: -0.32s;
                            }

                            @keyframes bobDot {
                                0%,
                                80%,
                                100% {
                                    transform: scale(0);
                                }
                                40% {
                                    transform: scale(1);
                                }
                            }
                        `}
                    />
                    <span
                        css={css`
                            display: inline-block;
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background-color: #28637e;
                            margin: 0 1px;
                            animation: bobDot 1.4s infinite ease-in-out both;
                            animation-delay: -0.16s;
                        `}
                    />
                    <span
                        css={css`
                            display: inline-block;
                            width: 4px;
                            height: 4px;
                            border-radius: 50%;
                            background-color: #28637e;
                            margin: 0 1px;
                            animation: bobDot 1.4s infinite ease-in-out both;
                        `}
                    />
                </span>
            </div>
        </div>
    );

    return (
        <>
            <div css={widgetContainer}>
                {isOpen && channelId && (
                    <div css={widgetPanel}>
                        <div
                            css={css`
                                display: flex;
                                flex-direction: column;
                                height: 100%;
                            `}
                        >
                            <div
                                className="d-flex flex-row align-items-center"
                                css={css`
                                    padding: 8px 12px;
                                    font-weight: bold;
                                    background-color: #50aeb0;
                                    color: #f7ffff;
                                    font-size: 14px;
                                    white-space: nowrap;
                                    overflow: hidden;
                                `}
                            >
                                {activeChannel && activeChannel?.teamData && (
                                    <Avatar item={activeChannel?.teamData || {}} />
                                )}

                                <div
                                    css={css`
                                        padding: 8px 12px;
                                        font-weight: bold;
                                        background-color: #50aeb0;
                                        color: #f7ffff;
                                        font-size: 14px;
                                        white-space: nowrap;
                                        overflow: hidden;
                                        text-overflow: ellipsis;
                                        display: flex;
                                        flex-direction: row;
                                        flex: 1;
                                    `}
                                >
                                    {activeChannel?.title && (
                                        <GoToChannel channel={activeChannel} titleAsLink styles={{ color: '#fff' }} />
                                    )}
                                    <button
                                        css={switchChannelButton}
                                        onClick={() => setShowChannelModal(true)}
                                        title="Switch channel"
                                    >
                                        <HiOutlineSwitchHorizontal size={20} />
                                    </button>
                                </div>

                                <div title="Close">
                                    <BiMinus
                                        css={css`
                                            background-color: transparent;
                                            border: none;
                                            cursor: pointer;
                                            font-size: 24px;
                                            color: #fff !important;
                                        `}
                                        onClick={() => {
                                            setIsOpen(false);
                                            toggleBobChat();
                                        }}
                                    />
                                </div>
                            </div>

                            <div
                                css={css`
                                    flex: 1;
                                    overflow-y: auto;
                                    padding: 8px 12px;
                                    background-color: #f5f5f5;
                                `}
                                ref={messagesContainerRef}
                            >
                                {localMessages.length === 0 ? (
                                    <div
                                        css={css`
                                            height: 100%;
                                            display: flex;
                                            align-items: flex-start;
                                            justify-content: flex-start;
                                            text-align: left;
                                            color: #222;
                                            font-size: 14px;
                                            padding-top: 8px;
                                        `}
                                    >
                                        <div
                                            css={css`
                                                max-width: 80%;
                                                padding: 6px 10px;
                                                border-radius: 12px;
                                                background-color: #ffffff;
                                                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
                                            `}
                                        >
                                            Please start a conversation with Bob!
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {localMessages
                                            .sort((m1, m2) => new Date(m1.createdAt) - new Date(m2.createdAt))
                                            .map((m) => {
                                                if (!m.text) {
                                                    return null;
                                                }
                                                const isSender = m.isCurrentUser;

                                                return (
                                                    <div
                                                        key={m.id}
                                                        css={css`
                                                            margin-bottom: 6px;
                                                            display: flex;
                                                            justify-content: ${isSender ? 'flex-end' : 'flex-start'};
                                                        `}
                                                    >
                                                        {!isSender && (
                                                            <div className="mr-2">
                                                                <Avatar item={m?.user || {}} />
                                                            </div>
                                                        )}

                                                        <div
                                                            css={css`
                                                                max-width: 80%;
                                                                padding: 6px 10px;
                                                                margin-bottom: 8px;
                                                                border-radius: 12px;
                                                                font-size: 13px;
                                                                white-space: pre-wrap;
                                                                word-wrap: break-word;
                                                                background-color: ${isSender ? '#28637e' : '#ffffff'};
                                                                color: ${isSender ? '#ffffff' : '#222'};
                                                            `}
                                                        >
                                                            <SanitizeHTML html={m.text} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        {isBobThinking && <ThinkingIndicator />}
                                    </>
                                )}
                            </div>
                            <form
                                onSubmit={handleSubmit}
                                css={css`
                                    padding: 8px 10px;
                                    border-top: 1px solid #ddd;
                                    background: #fff;
                                    display: flex;
                                    gap: 6px;
                                `}
                            >
                                <input
                                    type="text"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    placeholder="Type a message for Bob..."
                                    css={css`
                                        flex: 1;
                                        border-radius: 999px;
                                        border: 1px solid #ccc;
                                        padding: 6px 10px;
                                        font-size: 13px;
                                    `}
                                />
                                {activeChannel && activeChannel?.teamData && (
                                    <button
                                        type="submit"
                                        css={css`
                                            background: #50aeb0;
                                            color: #fff;
                                            border: none;
                                            border-radius: 999px;
                                            padding: 6px 12px;
                                            font-size: 13px;
                                            cursor: pointer;
                                        `}
                                    >
                                        Send
                                    </button>
                                )}
                            </form>
                        </div>
                    </div>
                )}
            </div>
            {/* <FabMenuContainer title="Chat with Bob">
                <StyledCreateChannelToggle onClick={handleToggleWidget} className={isOpen ? 'active' : ''}>
                    <span>
                        <FaRobot color="white" fontSize={30} />
                    </span>
                </StyledCreateChannelToggle>
            </FabMenuContainer> */}

            <Modal show={showChannelModal} onHide={() => setShowChannelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Select Channel for Bob Chat</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">Choose a channel to chat with Bob:</label>
                        <DynamicChannelSelectFilter
                            selectedOption={selectedChannelOption}
                            onRoomSelect={handleChannelSelect}
                            assignedChannel={selectedChannelOption?.value}
                            placeholder="Search and select a channel..."
                            teamIds={(teams || []).map((t) => t._id)}
                            permissionRule="get:chatrooms"
                        />
                    </div>
                    <div className="mt-3 text-muted small">
                        <p>
                            Current channel: <strong>{activeChannel?.title || 'Not selected'}</strong>
                        </p>
                        <p>Select a channel to switch Bob's chat context.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowChannelModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            if (selectedChannelOption && selectedChannelOption.value) {
                                handleChannelSelect(selectedChannelOption);
                            } else {
                                toastWrapper.error('Please select a channel first');
                            }
                        }}
                    >
                        Switch Channel
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});

const mapStateToProps = (state) => ({
    position: state.user.position,
    currUser: state.user.user,
    teams: state.teams.teams,
});

const BobChatWidget = (props) => (
    <ComponentErrorBoundary>
        <Component {...props} />
    </ComponentErrorBoundary>
);
export default connect(mapStateToProps, {})(BobChatWidget);
